/**
 * English translation resources (the default/fallback language). French lives
 * in ./fr.ts and is type-checked against this object's shape, so a missing key
 * is a compile error. Add new UI strings here first, then translate in fr.ts.
 */
export const en = {
  nav: {
    dashboard: 'Dashboard',
    guests: 'Guests',
    bookings: 'Bookings',
    rooms: 'Rooms',
    reports: 'Reports',
    channels: 'Channels',
    myProfile: 'My profile',
    addProperty: 'Add property',
    propertyProfile: 'Property profile',
    members: 'Members & invitations',
    dataCleanup: 'Data cleanup',
    verifyReceipt: 'Verify receipt',
    notifications: 'Notifications',
    auditLog: 'Audit log',
    platformAdmin: 'Platform admin',
  },
  notifications: {
    title: 'Notifications',
    subtitle: 'Booking, payment and financial activity in this workspace.',
    bellAria: 'Notifications, {{count}} unread',
    markAllRead: 'Mark all as read',
    loading: 'Loading notifications...',
    error: 'Something went wrong loading notifications.',
    empty: 'No notifications yet.',
    byActor: 'by {{name}}',
    openBooking: 'Open {{code}}',
    noDetails: 'No further details.',
    pageOf: 'Page {{page}} of {{total}}',
    columns: {
      field: 'Field',
      before: 'Before',
      after: 'After',
    },
    events: {
      booking_created: 'Booking created',
      booking_updated: 'Booking edited',
      booking_cancelled: 'Booking cancelled',
      booking_deleted: 'Booking deleted',
      payment_recorded: 'Payment recorded',
      payment_updated: 'Payment edited',
      payment_deleted: 'Payment deleted',
      financial_updated: 'Financial record edited',
      unknown: 'Activity',
    },
    fields: {
      check_in_date: 'Check-in date',
      check_out_date: 'Check-out date',
      estimated_check_in_time: 'Check-in time',
      estimated_check_out_time: 'Check-out time',
      adults: 'Adults',
      children: 'Children',
      room_id: 'Room',
      booking_channel_id: 'Channel',
      status: 'Status',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
      stay_purpose: 'Stay purpose',
      amount: 'Amount',
      type: 'Type',
      method: 'Method',
      received_at: 'Received at',
      expected_amount: 'Expected amount',
      actual_amount: 'Agreed amount',
      notes: 'Note',
    },
  },
  shell: {
    greetingMorning: 'Good morning',
    greetingAfternoon: 'Good afternoon',
    greetingEvening: 'Good evening',
    signOut: 'Sign out',
    offlineBanner: "You're offline. You can keep working — changes will sync automatically when your connection returns.",
    refresh: 'Refresh app',
    back: 'Go back',
    forward: 'Go forward',
    collapseNav: 'Collapse navigation',
    expandNav: 'Expand navigation',
    openMenu: 'Open navigation menu',
    closeMenu: 'Close navigation menu',
    checkingSession: 'Checking your session...',
    subtitle: 'Operations console',
    primaryNav: 'Primary',
    adminNav: 'Administration',
    selectedProperty: 'Selected property',
    property: 'Property',
    cachedData: 'Cached data',
    statusOffline: 'Offline',
    statusSyncing: 'Syncing',
    statusConflicts_one: '{{count}} conflict',
    statusConflicts_other: '{{count}} conflicts',
    statusFailed: '{{count}} failed',
    statusQueued: '{{count}} queued',
    dismissInstallBanner: 'Dismiss install banner',
  },
  camera: {
    title: 'Take a photo',
    preview: 'Camera preview',
    switch: 'Switch camera',
    capture: 'Capture',
    saving: 'Saving...',
    errors: {
      blocked: 'Camera access was blocked. Allow camera access in your browser and try again.',
      notFound: 'No camera was found on this device.',
      inUse: 'The camera is already in use by another app.',
      generic: 'We could not start the camera. Please try again.',
      notReady: 'The camera is not ready yet. Please wait a moment and try again.',
      captureFailed: 'We could not capture the photo on this device.',
      captureRetry: 'We could not capture the photo. Please try again.',
    },
  },
  savedFilters: {
    label: 'Saved filter',
    none: 'No saved filter',
    apply: 'Apply',
    name: 'Filter name',
    save: 'Save',
    update: 'Update selected filter',
    delete: 'Delete selected filter',
    createdToast: 'Saved filter created.',
    updatedToast: 'Saved filter updated.',
    deletedToast: 'Saved filter deleted.',
    createError: 'Could not save this filter.',
    updateError: 'Could not update this filter.',
    deleteError: 'Could not delete this filter.',
  },
  guestSearch: {
    label: 'Guest',
    noMatches: 'No matching guests',
    typeToSearch: 'Type to search guests',
  },
  language: {
    label: 'Language',
    english: 'English',
    french: 'Français',
  },
  home: {
    login: 'Login',
    register: 'Register',
    heroTitle: 'Manage every stay from one secure workspace.',
    heroSubtitle:
      'Built for hotels, guest houses, lodges, and short-stay properties that need bookings, guests, payments, reviews, and staff access in one place.',
    heroRegisterCta: 'Register property workspace',
    heroLoginCta: 'Login to workspace',
    closeMenu: 'Close navigation menu',
    stepAria: 'Step {{number}}: {{title}}',
    theme: 'Theme',
    mobileNav: 'Mobile navigation',
    onboarding: {
      eyebrow: 'Onboarding process',
      description:
        'The first-run path stays explicit: email code verification, secure account setup, property workspace setup, then daily hospitality operations.',
      title: 'From property registration to live operations in four defined steps',
      step1Title: 'Verify the administrator email',
      step1Body: 'Enter an email address and confirm the 5-minute registration code.',
      step2Title: 'Create secure account access',
      step2Body: 'Complete the account profile with phone number and a strong password.',
      step3Title: 'Register the property workspace',
      step3Body: 'Capture the property profile or accept an invitation from an existing team.',
      step4Title: 'Launch hospitality operations',
      step4Body: 'Invite staff and start managing stays, guests, payments, and reviews.',
    },
    value: {
      eyebrow: 'Value proposition',
      title: 'A calmer operating platform for modern hospitality teams',
      description:
        'Nang Book gives property teams one cloud workspace for stays, guests, payments, access, and accountability.',
      prop1Title: 'Run every stay from one record',
      prop1Body: 'Bookings, guest details, check-in readiness, and reviews stay in one cloud view.',
      prop2Title: 'Support every property type',
      prop2Body: 'Hotels, guest houses, lodges, and short-stay teams can work from the same platform.',
      prop3Title: 'Protect finance and audit work',
      prop3Body: 'Payment status, sensitive actions, and accountability are handled with tighter controls.',
    },
    access: {
      resetPassword: 'Reset password',
      resendReset: 'Resend reset link',
      resendVerification: 'Resend verification',
      registerProperty: 'Register property',
    },
    signals: {
      stays: 'Stays',
      staysHelper: 'today',
      payments: 'Payments',
      paymentsValue: 'Clear',
      paymentsHelper: 'finance view',
      access: 'Access',
      accessValue: 'Scoped',
      accessHelper: 'role based',
      bookingSync: 'Booking sync',
      financeReview: 'Finance review',
      auditTrail: 'Audit trail',
    },
    testimonials: {
      eyebrow: 'Customer voices',
      title: 'Trusted by property teams across different hospitality models',
      description:
        'A mix of hotels, guest houses, lodges, and short-stay operators use Nang Book to keep daily work clear.',
      label: 'Testimonial',
      region: 'Customer testimonials',
      stars: 'Five star testimonial',
      position: '{{current}} of {{total}}',
      previous: 'Previous testimonial',
      next: 'Next testimonial',
      show: 'Show testimonial from {{name}}',
      role1: 'General Manager, Accra City Suites',
      quote1:
        'Nang Book gives our reception, finance, and housekeeping teams the same picture of every stay without chasing spreadsheets.',
      role2: 'Owner, Harbor Lane Guest House',
      quote2:
        'The onboarding flow made it clear what to do first. We registered, verified access, and had our team working from one dashboard.',
      role3: 'Operations Lead, Enugu Short Stays',
      quote3:
        'Guest records, payments, and staff access feel organized. It is much easier to hand over shifts without missing details.',
      role4: 'Director, Northgate Lodge',
      quote4:
        'The recovery and verification flows are simple enough for seasonal staff, but controlled enough for management oversight.',
      role5: 'Founder, Rift Valley Retreats',
      quote5:
        'We run several small properties, and Nang Book keeps each team focused on arrivals, guests, and accountability.',
    },
  },
  common: {
    genericError: 'Something went wrong. Please try again.',
    login: 'Login',
    print: 'Print',
    export: 'Export',
    exporting: 'Exporting...',
    exportCsv: 'Export as CSV',
    exportXlsx: 'Export as XLSX',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    saveChanges: 'Save changes',
    edit: 'Edit',
    add: 'Add',
    delete: 'Delete',
    deleting: 'Deleting...',
    clear: 'Clear',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    yes: 'Yes',
    no: 'No',
    close: 'Close',
    actions: 'Actions',
    actionsForAria: 'Actions for {{name}}',
    unassigned: 'Unassigned',
    emptyListAction: 'This action cannot be performed on an empty list.',
    nothingToPrint: 'There is nothing to print.',
    correctFields: 'Please correct the highlighted fields.',
    optional: 'Optional.',
    phoneHelper: 'Pick the country, then enter the number.',
  },
  field: {
    email: 'Email',
    password: 'Password',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    confirmNewPassword: 'Confirm new password',
    fullName: 'Full name',
    country: 'Country',
    mobileNumber: 'Mobile number',
    whatsappNumber: 'WhatsApp number',
    verificationCode: 'Verification code',
    placeOfOrigin: 'Place of origin',
    nationality: 'Nationality',
    gender: 'Gender',
    dateOfBirth: 'Date of birth',
    primaryPhone: 'Primary phone',
    secondaryPhone: 'Secondary phone',
    guestCode: 'Guest code',
    phone: 'Phone',
    status: 'Status',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    bookingCode: 'Booking code',
    guest: 'Guest',
    channel: 'Channel',
    room: 'Room',
    checkInDate: 'Check-in date',
    checkOutDate: 'Check-out date',
    estCheckInTime: 'Estimated check-in time',
    estCheckOutTime: 'Estimated checkout time',
    adults: 'Adults',
    children: 'Children',
    expectedAmount: 'Expected amount',
    bookingChannel: 'Booking channel',
    stayPurpose: 'Stay purpose',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
    from: 'From',
    to: 'To',
    notes: 'Notes',
    amount: 'Amount',
    balance: 'Balance',
    comment: 'Comment',
    name: 'Name',
    role: 'Role',
    expires: 'Expires',
    mobile: 'Mobile',
    whatsapp: 'WhatsApp',
    address: 'Address',
    website: 'Website',
    codePrefix: 'Code prefix',
    contactPerson: 'Contact person',
    contactNumber: 'Contact number',
  },
  documents: {
    fields: {
      chooseFile: 'Choose file',
      takePhoto: 'Take photo',
      documentType: 'Document type',
      documentNumber: 'Document number',
      issuingCountry: 'Issuing country',
      issuingAuthority: 'Issuing authority',
      issueDate: 'Issue date',
      expiryDate: 'Expiry date',
    },
  },
  enum: {
    status: {
      pending: 'Pending',
      confirmed: 'Confirmed',
      checked_in: 'Checked in',
      checked_out: 'Checked out',
      cancelled: 'Cancelled',
      no_show: 'No show',
    },
    stayPurpose: {
      vacation: 'Vacation',
      event: 'Event',
      accommodation: 'Accommodation',
      business: 'Business',
      family_visit: 'Family visit',
      other: 'Other',
    },
    paymentMethod: {
      cash: 'Cash',
      mobile_pay: 'Mobile pay',
      card: 'Card',
      bank_transfer: 'Bank transfer',
      paypal: 'PayPal',
    },
    paymentType: {
      charge: 'Charge',
      refund: 'Refund',
    },
    documentType: {
      passport: 'Passport',
      national_id: 'National ID',
      drivers_license: "Driver's license",
      residence_permit: 'Residence permit',
      other: 'Other',
    },
    paymentStatus: {
      unpaid: 'Unpaid',
      partially_paid: 'Partially paid',
      paid: 'Paid',
      refunded: 'Refunded',
      overpaid: 'Overpaid',
    },
    commentType: {
      complaint: 'Complaint',
      feedback: 'Feedback',
      internal_note: 'Internal note',
      payment_note: 'Payment note',
      maintenance_note: 'Maintenance note',
      reservation_note: 'Reservation note',
    },
    tokenAction: {
      view_booking: 'View booking',
      submit_review: 'Submit review',
    },
    roomStatus: {
      available: 'Available',
      occupied: 'Occupied',
      cleaning: 'Cleaning',
      maintenance: 'Maintenance',
      out_of_service: 'Out of service',
    },
    role: {
      systemAdministrator: 'System Administrator',
      manager: 'Manager',
      frontDeskStaff: 'Front Desk Staff',
      financeUser: 'Finance User',
    },
    membershipStatus: {
      active: 'active',
      invited: 'invited',
      disabled: 'disabled',
    },
  },
  auth: {
    login: {
      title: 'Login',
      subtitle: 'Use your staff account to continue.',
      submit: 'Sign in',
      submitting: 'Signing in...',
      signedIn: 'Signed in successfully.',
      newHere: 'New here?',
      createAccount: 'Create an account',
      forgotPassword: 'Forgot your password?',
      requestReset: 'Request reset',
      needVerification: 'Need a verification email?',
      resendVerification: 'Resend verification',
    },
    register: {
      title: 'Create your account',
      subtitle: 'Get started by verifying your email.',
      emailHelper: 'We will send a one-time registration code.',
      sendCode: 'Send code',
      sendingCode: 'Sending code...',
      otpPrompt: 'Enter the 6-digit code sent to {{email}}.',
      otpHelper: 'Codes expire after 5 minutes.',
      verifyCode: 'Verify code',
      verifyingCode: 'Verifying code...',
      resend: 'Resend code',
      resending: 'Resending...',
      resendCountdown: 'Resend code ({{seconds}}s)',
      changeEmail: 'Change email',
      mobileHelper: 'Pick the country, then enter the number.',
      whatsappHelper: 'Defaults to the mobile number when left blank.',
      createAccount: 'Create account',
      creatingAccount: 'Creating account...',
      alreadyRegistered: 'Already registered?',
      login: 'Login',
      otpSent: 'If this email can be registered, a verification code has been sent.',
      sendCodeError: 'We could not send the verification code. Please try again.',
      emailVerified: 'Email verified. Continue creating your account.',
      verifyCodeError: 'We could not verify this code. Please try again.',
      resendError: 'We could not resend the verification code. Please try again.',
      accountCreatedWelcome: 'Account created. Welcome to Nang Book!',
      accountCreatedLogin: 'Account created. Please log in to continue.',
    },
    forgot: {
      title: 'Forgot password',
      subtitle: 'Request a secure reset link for your staff account.',
      submit: 'Send reset link',
      submitting: 'Sending link...',
      sent: 'Password reset instructions have been sent if the email exists.',
      haveLink: 'Already have a reset link? Open it from your email to continue.',
      didNotReceive: 'Did not receive it?',
      resend: 'Resend reset link',
    },
    reset: {
      title: 'Reset password',
      subtitle: 'Set a new password from your reset email.',
      submit: 'Reset password',
      submitting: 'Resetting password...',
      done: 'Password reset. You can now log in.',
      missingDetails: 'This reset link is missing required details.',
      requestNew: 'Request a new password reset link and open it from your email.',
      needNewLink: 'Need a new link?',
      resend: 'Resend reset link',
    },
    resendVerification: {
      title: 'Resend verification',
      subtitle: 'Send another email verification link to an administrator.',
      submit: 'Resend verification',
      submitting: 'Sending verification...',
      sent: 'A new verification email has been sent if the account exists.',
      alreadyVerified: 'Email already verified?',
    },
    resendReset: {
      title: 'Resend reset link',
      subtitle: 'Send another password reset email to a staff account.',
      submit: 'Resend reset link',
      submitting: 'Resending link...',
      sent: 'A new reset link has been sent if the email exists.',
      remembered: 'Remembered the password?',
    },
    verifyEmail: {
      title: 'Verify email',
      subtitle: 'Confirm the administrator email before using the workspace.',
      intro: 'If you opened this page from a verification email, continue to complete verification. If the link expired, request a new verification email.',
      submit: 'Verify email',
      submitting: 'Verifying email...',
      done: 'Email verified. You can now log in.',
      needAnother: 'Need another verification email?',
      resend: 'Resend verification',
    },
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: "Today's lodge operations",
    loading: 'Loading dashboard...',
    error: 'Something went wrong loading the dashboard. Please try again.',
    welcome: 'Welcome',
    welcomeNamed: 'Welcome, {{name}}',
    metrics: {
      arrivalsToday: 'Arrivals today',
      departuresToday: 'Departures today',
      currentlyCheckedIn: 'Currently checked in',
      upcomingArrivals: 'Upcoming arrivals (7 days)',
      needsCleanup: 'Needs cleanup',
      collectedThisMonth: 'Collected this month',
      agreedThisMonth: 'Agreed this month',
    },
    limited: {
      subtitle: "Here's what you can jump into.",
      guestsDesc: 'Find and manage guest records and contact details.',
      bookingsDesc: 'View and manage reservations, check-ins, and check-outs.',
      open: 'Open {{label}}',
      noAccess: 'Use the menu to navigate to the areas you have access to.',
    },
    getStarted: {
      subtitle: "Let's get your workspace ready. Create a property to unlock your dashboard.",
      createTitle: 'Create your property',
      createDesc: 'Set up your hotel, guest house, or lodge to start managing bookings, guests, rooms, and reports.',
      createCta: 'Create property',
      profileTitle: 'Complete your profile',
      profileDesc: 'Update your name and phone number, and set a strong password for your account.',
      profileCta: 'Edit profile',
      invite: 'Once you have created a property, you can invite staff and assign their roles from Members & invitations.',
    },
  },
  guests: {
    title: 'Guests',
    subtitle: 'Guest records and contact history',
    refresh: 'Refresh guests',
    add: 'Add guest',
    search: 'Search guests',
    searchPlaceholder: 'Name, guest code, email, or phone',
    loading: 'Loading guests...',
    error: 'Something went wrong loading guests. Please try again.',
    empty: 'No guests found.',
    exportError: 'Could not export guests. Please try again.',
    newSubtitle: 'The guest code is generated automatically on save.',
    createSubmit: 'Create guest',
    creating: 'Creating...',
    createdToast: 'Guest {{code}} created. Continue with their booking.',
    detailLoading: 'Loading guest...',
    detailError: 'Something went wrong loading this guest. Please try again.',
    updatedToast: 'Guest updated.',
    deletedToast: 'Guest deleted.',
    deleteError: 'Could not delete this guest. Please try again.',
    bookingHistory: 'Booking history',
    noBookings: 'No bookings on file for this guest yet.',
    receipt: 'Receipt',
    getReceipt: 'Get receipt',
    getReceiptAria: 'Get receipt for {{code}}',
    preparingReceipt: 'Preparing...',
    receiptDownloadError: 'Could not download the receipt PDF.',
    deleteTitle: 'Delete this guest?',
    deleteBody:
      'This removes {{name}} from active guest lists. Booking history is preserved for record-keeping and this can be reversed by an administrator if needed.',
    profile: {
      male: 'Male',
      female: 'Female',
      validation: {
        fullNameRequired: 'Full name is required.',
        genderRequired: 'Gender is required.',
        placeOfOriginRequired: 'Place of origin is required.',
        nationalityRequired: 'Nationality is required.',
        dateOfBirthRequired: 'Date of birth is required.',
        countryFromList: 'Choose a country from the list.',
        minimumAge: 'The guest must be at least {{years}} years old.',
      },
    },
    documents: {
      requiredForCheckIn:
        'This guest cannot be checked in until their ID is recorded — still missing: {{fields}}. Tell the guest we are required to record this for regulatory purposes.',
      fieldLabel: {
        document_type: 'document type',
        document_number: 'document number',
        issuing_country: 'issuing country',
      },
      title: 'Identification documents',
      loading: 'Loading documents...',
      error: 'Something went wrong loading documents. Please try again.',
      empty: 'No identification documents uploaded yet.',
      submit: 'Submit',
      update: 'Update',
      addTitle: 'Add identification document',
      addHint:
        'Fill in the details of the guest information from the image of their document that you received during onboarding. Attaching an image here is optional.',
      editTitle: 'Edit identification document',
      editHint: 'Complete or correct the details. Pick a new file only if you want to replace the current one.',
      replaceFile: 'Replace file',
      savedToast: 'Document saved.',
      updatedToast: 'Document updated.',
      deletedToast: 'Document deleted.',
      saveError: 'Could not save the document. Please try again.',
      updateError: 'Could not update the document. Please try again.',
      deleteError: 'Could not delete the document.',
      downloadError: 'Could not download the document. Please try again.',
      editAria: 'Edit {{type}}',
      downloadAria: 'Download {{type}}',
      deleteAria: 'Delete {{type}}',
      validation: {
        typeRequired: 'Document type is required.',
        expiryAfterIssue: 'Expiry date must be on or after the issue date.',
      },
    },
  },
  bookings: {
    title: 'Bookings',
    subtitle: 'Stay records, filtered by date, channel, status, and purpose',
    refresh: 'Refresh bookings',
    add: 'Add booking',
    search: 'Search bookings',
    searchPlaceholder: 'Booking code, guest, room, or channel',
    loading: 'Loading bookings...',
    error: 'Something went wrong loading bookings. Please try again.',
    empty: 'No bookings match these filters.',
    exportError: 'Could not export bookings. Please try again.',
    receiptGenerateError: 'Could not generate the receipt PDF.',
    receiptEmailError: 'Could not email the receipt.',
    receiptEmailedToast: 'Receipt emailed to {{recipient}}.',
    receiptShareTitle: 'Receipt {{code}}',
    receiptShareText: 'Your receipt',
    receiptShareUnavailable: 'Sharing is not available here, so the receipt was downloaded instead.',
    receiptShareError: 'Could not share the receipt.',
    receipt: 'Receipt',
    downloadPdf: 'Download PDF',
    emailToGuest: 'Email to guest',
    share: 'Share',
    receiptAria: 'Receipt for {{code}}',
    newTitle: 'Add booking',
    newSubtitle: 'The booking code is generated automatically on save.',
    newSubtitleGuest:
      'Booking for {{name}} ({{code}}). The booking code is generated automatically on save.',
    detailLoading: 'Loading booking...',
    detailError: 'Something went wrong loading this booking. Please try again.',
    updatedToast: 'Booking updated.',
    deletedToast: 'Booking deleted.',
    deleteError: 'Could not delete this booking. Please try again.',
    deleteTitle: 'Delete this booking?',
    deleteBody:
      'This removes {{code}} from active booking lists. The record is preserved for history and can be restored by an administrator if needed.',
    nightsOccupants: 'Nights / occupants',
    nightsGuests: '{{nights}} nights · {{guests}} guests',
    stayPurposeLabel: 'Stay purpose',
    reviewAgreedAfterDateChange: 'Dates changed — review the agreed amount in the financial record.',
    cancelBooking: 'Cancel booking',
    cancelling: 'Cancelling...',
    cancelledToast: 'Booking cancelled. The room has been freed.',
    cancelError: 'Could not cancel this booking. Please try again.',
    cancelTitle: 'Cancel this booking?',
    cancelBody:
      'This frees the room for {{code}} and clears its expected, agreed and paid amounts. A booking that still holds guest money must be fully refunded first.',
    form: {
      selectGuest: 'Select a guest for this booking.',
      enterExpected: 'Enter the expected amount to pay.',
      estCheckInTime: 'Estimated check-in time',
      estCheckOutTime: 'Estimated checkout time',
      expectedHelper: 'List price for this booking (auto — accounting only).',
      loadingSelectedChannel: 'Loading selected channel...',
      roomHelperNone: 'No rooms are available for these dates. You can save now and assign a room later.',
      roomHelperOptional: 'Optional. Only rooms free for the selected dates are shown.',
      currentlyAssignedRoom: 'Currently assigned room',
      statusHelper: 'Defaults to pending when left blank.',
      statusDefault: 'Default',
    },
    wizard: {
      steps: {
        guest: 'Guest',
        basic: 'Basic',
        identity: 'Identity',
        stay: 'Stay',
        finance: 'Finances',
        comment: 'Comment',
        review: 'Review',
      },
      stepOf: 'Step {{current}} of {{total}}',
      loadingProperty: 'Loading property settings...',
      confirmBooking: 'Confirm booking',
      createdToast: 'Booking {{code}} created.',
      saveError: 'Could not save this booking.',
      checking: 'Checking...',
      stepCheckError: 'Could not check this step. Please try again.',
      completeRequired: 'Complete the required fields before continuing.',
      completeRequiredSave: 'Complete the required fields before saving the booking.',
      existingSelected: 'Existing guest selected: {{name}} ({{code}})',
      existingSelectedIncomplete:
        "{{name}} ({{code}}) is on file, but their profile is incomplete. The next two steps collect what's missing.",
      newProfileNote: 'A new guest profile will be created when this booking is confirmed.',
      registerNewGuest: 'Register new guest',
      fullLegalNames: 'Full legal names',
      gender: 'Gender',
      male: 'Male',
      female: 'Female',
      phoneNumber: 'Phone number',
      alternativeNumber: 'Alternative number',
      dateOfBirth: 'Date of birth',
      uploadIdPhoto: 'Upload ID photo',
      identityDetailsLaterNote:
        'Photograph or attach the ID now — only the document type is required. The remaining details can be completed later from the guest profile.',
      checkoutDate: 'Checkout date',
      reservationMedium: 'Reservation medium',
      allowedRange: 'Allowed {{min}}–{{max}}',
      loadingChannels: 'Loading channels...',
      loadingRooms: 'Loading rooms...',
      roomHelperNoneCreate: 'No rooms are available for these dates. You can create the booking and assign a room later.',
      perNight: '{{amount}}/night',
      purpose: 'Purpose',
      comment: 'Comment',
      paymentType: 'Payment type',
      actualAmount: 'Actual amount',
      amountPaidNow: 'Amount paid now',
      expectedHelperRoom: 'Room rate × {{count}} nights (edit the actual amount if negotiated).',
      review: {
        identity: 'Identity',
        idPhoto: 'ID photo',
        stay: 'Stay',
        occupants: 'Occupants',
        expectedList: 'Expected amount (list)',
        agreedDue: 'Agreed amount (due)',
        paidNow: 'Paid now',
        occupantsValue: '{{adults}} adults, {{children}} children',
        stayValue: '{{checkIn}} {{checkInTime}} to {{checkOut}} {{checkOutTime}}',
        existingGuestRecord: 'Existing guest record',
        onExistingRecord: 'On existing guest record',
        attached: 'Attached ({{name}})',
        notAttached: 'Not attached',
        identityValue: '{{type}} {{number}}',
      },
      validation: {
        selectGuestOrRegister: 'Select an existing guest or register a new guest.',
        fullLegalNamesRequired: 'Full legal names are required.',
        genderRequired: 'Gender is required.',
        placeOfOriginRequired: 'Place of origin is required.',
        nationalityRequired: 'Nationality is required.',
        dateOfBirthRequired: 'Date of birth is required.',
        idTypeRequired: 'ID type is required.',
        expiryAfterIssue: 'Expiry date must be on or after the issue date.',
        adultsRequired: 'Adults are required.',
        childrenRequired: 'Children are required.',
        checkInDateRequired: 'Check-in date is required.',
        estCheckInTimeRequired: 'Estimated check-in time is required.',
        checkoutDateRequired: 'Checkout date is required.',
        estCheckoutTimeRequired: 'Estimated checkout time is required.',
        reservationMediumRequired: 'Reservation medium is required.',
        purposeRequired: 'Purpose is required.',
        atLeastOneAdult: 'At least one adult is required.',
        childrenNegative: 'Children cannot be negative.',
        checkoutAfterCheckin: 'Checkout date must be later than check-in date.',
        paymentTypeRequired: 'Payment type is required.',
        expectedRequired: 'Expected amount is required.',
        actualRequired: 'Actual amount is required.',
        amountPaidNowRequired: 'Amount paid now is required.',
        expectedNegative: 'Expected amount cannot be negative.',
        actualNegative: 'Actual amount cannot be negative.',
        amountPaidNowNegative: 'Amount paid now cannot be negative.',
        amountPaidExceedsActual: 'Amount paid now cannot be greater than the actual amount.',
        timeRangeCheckIn: 'Estimated check-in time must be between {{start}} and {{end}}.',
        timeRangeCheckout: 'Estimated checkout time must be between {{start}} and {{end}}.',
      },
    },
    panels: {
      financial: {
        title: 'Financial record',
        noPermission: "You don't have permission to view financial data.",
        loading: 'Loading financial record...',
        error: 'Something went wrong loading the financial record.',
        empty: 'No financial record yet.',
        expectedList: 'Expected amount (list price)',
        agreedDue: 'Agreed amount (total due)',
        amountPaid: 'Amount paid',
        paymentStatus: 'Payment status',
        discountServiceFee: 'Discount / service fee',
        expectedAmountCurrency: 'Expected amount ({{currency}})',
        expectedHelper: 'Auto list price — accounting only.',
        agreedAmountCurrency: 'Agreed amount ({{currency}})',
        agreedHelper: 'Total due — cannot be below what is already paid.',
        statusOverride: 'Payment status override',
        statusOverrideHelper: "Leave blank to let the system derive it from the amounts; only 'Refunded' is a manual designation.",
        deriveAuto: 'Derive automatically',
        saveRecord: 'Save financial record',
        savedToast: 'Financial record saved.',
        saveError: 'Could not save the financial record.',
      },
      payments: {
        title: 'Payments',
        noPermission: "You don't have permission to view payments.",
        record: 'Record payment',
        editTitle: 'Edit payment',
        totalDue: 'Total due',
        amountRefunded: 'Amount refunded',
        fullyPaid: 'This booking is fully paid — no further payment can be added.',
        loading: 'Loading payments...',
        error: 'Something went wrong loading payments.',
        empty: 'No payments recorded yet.',
        method: 'Method',
        type: 'Type',
        receivedBy: 'Received by',
        receivedAt: 'Received at',
        editAria: 'Edit payment {{id}}',
        deleteAria: 'Delete payment {{id}}',
        amountCurrency: 'Amount ({{currency}})',
        cannotExceed: 'Cannot exceed the {{amount}} still outstanding.',
        outstanding: 'Outstanding balance: {{amount}}.',
        refundable: 'At most {{amount}} can be refunded.',
        receivedAtHelper: 'Defaults to now if left blank.',
        receivedByHelper: 'Who collected this money. Defaults to you.',
        meDefault: 'Me (default)',
        reference: 'Reference',
        recordedToast: 'Payment recorded.',
        updatedToast: 'Payment updated.',
        saveError: 'Could not save this payment.',
        deletedToast: 'Payment deleted.',
        deleteError: 'Could not delete this payment.',
        deleteTitle: 'Delete this payment?',
        deleteBody: "This removes the payment and recalculates the booking's financial record.",
      },
      review: {
        title: 'Review',
        noPermission: "You don't have permission to view reviews.",
        sendRequest: 'Send review request',
        sending: 'Sending...',
        loading: 'Loading review...',
        error: 'Something went wrong loading the review.',
        empty: 'No review submitted yet.',
        marked: 'Marked as reviewed',
        notMarked: 'Not yet marked as reviewed',
        rating: 'Rating',
        markReviewed: 'Mark as reviewed',
        saveReview: 'Save review',
        requestEmailed: 'Review request emailed to the guest.',
        requestLinkOnly: 'Review request link created, but the email could not be sent — share the link manually.',
        requestError: 'Could not send a review request.',
        savedToast: 'Review saved.',
        saveError: 'Could not save the review.',
      },
      comments: {
        title: 'Comments',
        noPermission: "You don't have permission to view comments.",
        loading: 'Loading comments...',
        error: 'Something went wrong loading comments.',
        empty: 'No comments yet.',
        unknown: 'Unknown',
        enterComment: 'Enter a comment before submitting.',
        addedToast: 'Comment added.',
        addError: 'Could not add the comment.',
        type: 'Type',
        addPlaceholder: 'Add a comment',
        adding: 'Adding...',
        add: 'Add comment',
      },
      links: {
        title: 'Guest booking links',
        noPermission: "You don't have permission to manage booking links.",
        create: 'Create link',
        loading: 'Loading links...',
        error: 'Something went wrong loading booking links.',
        empty: 'No guest links created for this booking yet.',
        revoked: 'Revoked',
        active: 'Active',
        expired: 'Expired',
        expires: 'Expires {{date}}',
        revoking: 'Revoking...',
        revoke: 'Revoke',
        createTitle: 'Create a guest booking link',
        oncePrompt: 'This link is only shown once — copy it now and share it with the guest.',
        linkLabel: 'Link',
        copyLink: 'Copy link',
        expiresInDays: 'Expires in (days)',
        expiryHelper: 'Leave blank to use the default expiry.',
        creating: 'Creating...',
        createError: 'Could not create a booking link.',
        revokedToast: 'Link revoked.',
        revokeError: 'Could not revoke this link.',
        copiedToast: 'Link copied to clipboard.',
        copyError: 'Could not copy the link automatically — select and copy it manually.',
      },
      receipts: {
        title: 'Receipt',
        title403: 'Receipts',
        noPermission: "You don't have permission to view billing documents.",
        preparing: 'Preparing...',
        download: 'Download receipt',
        email: 'Email receipt',
        loading: 'Loading the receipt...',
        error: 'Something went wrong loading the receipt.',
        empty: 'No receipt has been issued yet. Use “Download receipt” or “Email receipt” to generate it.',
        number: 'Number',
        issuedBy: 'Issued by',
        issuedAt: 'Issued at',
        downloadAria: 'Download {{number}} as PDF',
        emailAria: 'Email {{number}} to the guest',
        shareAria: 'Share {{number}}',
        deleteAria: 'Delete {{number}}',
        deletedToast: 'Receipt deleted.',
        deleteError: 'Could not delete this receipt.',
        deleteTitle: 'Delete {{number}}?',
        deleteBody: 'This removes the receipt from this booking. It will be regenerated the next time it is downloaded or a payment changes.',
      },
    },
  },
  rooms: {
    title: 'Rooms',
    subtitle: 'Manage room inventory, operating status, capacity, base rates, and dated sell rates.',
    refresh: 'Refresh rooms',
    add: 'Add room',
    search: 'Search rooms',
    allStatuses: 'All statuses',
    loading: 'Loading rooms...',
    error: 'Something went wrong loading rooms. Please try again.',
    empty: 'No rooms match these filters.',
    createdToast: 'Room created.',
    updatedToast: 'Room updated.',
    deletedToast: 'Room deleted.',
    saveError: 'Could not save this room.',
    deleteError: 'Could not delete this room.',
    deleteConflict: 'This room is assigned to active bookings. Unassign those bookings or mark the room out of service.',
    addTitle: 'Add room',
    editTitle: 'Edit {{number}}',
    deleteTitle: 'Delete {{number}}?',
    deleteBody: 'This removes the room from active inventory. It can only be deleted when no active booking references it.',
    roomNumber: 'Room number',
    roomType: 'Room type',
    displayName: 'Display name',
    floor: 'Floor',
    baseRateCurrency: 'Base rate ({{currency}})',
    baseRate: 'Base rate',
    room: 'Room',
    type: 'Type',
    capacity: 'Capacity',
    viewScheduleAria: 'View schedule for {{number}}',
    manageRatesAria: 'Manage rates for {{number}}',
    viewSchedule: 'View schedule',
    manageRates: 'Manage rates',
    editAria: 'Edit {{number}}',
    deleteAria: 'Delete {{number}}',
    rates: {
      title: 'Rates for {{number}}',
      rateName: 'Rate name',
      amountCurrency: 'Amount ({{currency}})',
      starts: 'Starts',
      ends: 'Ends',
      active: 'Active',
      addRate: 'Add rate',
      saveRate: 'Save rate',
      cancelEdit: 'Cancel edit',
      loading: 'Loading rates...',
      error: 'Something went wrong loading room rates.',
      empty: 'No rates saved for this room yet.',
      caption: 'Room rates',
      datesHeader: 'Dates',
      dates: '{{start}} to {{end}}',
      any: 'Any',
      activeChip: 'Active',
      inactiveChip: 'Inactive',
      editAria: 'Edit {{name}}',
      deleteAria: 'Delete {{name}}',
      createdToast: 'Room rate created.',
      updatedToast: 'Room rate updated.',
      deletedToast: 'Room rate deleted.',
      saveError: 'Could not save this room rate.',
      deleteError: 'Could not delete this room rate.',
    },
    detail: {
      loading: 'Loading room schedule...',
      error: 'Something went wrong loading this room. Please try again.',
      allRooms: 'All rooms',
      roomTitle: 'Room {{number}}',
      subtitleFallback: 'Room schedule and availability',
      refresh: 'Refresh schedule',
      guests: '{{count}} guests',
      availabilityCalendar: 'Availability calendar',
      upcomingBookings: 'Upcoming bookings',
      noUpcoming: 'No upcoming bookings for this room.',
      caption: 'Upcoming bookings for room {{number}}',
      booking: 'Booking',
      nights: 'Nights',
      prevMonth: 'Previous month',
      nextMonth: 'Next month',
      available: 'Available',
      occupied: 'Occupied',
      today: 'Today',
      cellAria: '{{date}}: {{state}}',
      nights_one: '{{count}} night',
      nights_other: '{{count}} nights',
      notAvailable: "This room is {{status}} and isn't available for booking.",
      availableNowNoBookings: 'Available now, with no upcoming bookings.',
      availableFromOnward: 'Available from {{date}} onward.',
      availableWindow: 'Available {{when}} for up to {{nights}} — the next guest checks in on {{date}}.',
      whenNow: 'now',
      whenOn: 'on {{date}}',
    },
  },
  settings: {
    channels: {
      title: 'Booking channels',
      subtitle: 'Manage the sources bookings can come from. Deactivate a channel to hide it from new bookings without losing historical records.',
      refresh: 'Refresh channels',
      add: 'Add channel',
      loading: 'Loading booking channels...',
      error: 'Something went wrong loading booking channels. Please try again.',
      empty: 'No booking channels yet. Add one to get started.',
      activeChip: 'Active',
      inactiveChip: 'Inactive',
      editAria: 'Edit {{name}}',
      deleteAria: 'Delete {{name}}',
      addTitle: 'Add booking channel',
      editTitle: 'Edit booking channel',
      active: 'Active',
      createdToast: 'Booking channel created.',
      updatedToast: 'Booking channel updated.',
      deletedToast: 'Booking channel deleted.',
      saveError: 'Could not save this booking channel.',
      deleteError: 'Could not delete this booking channel.',
      deleteConflict: 'This channel is still used by existing bookings and cannot be deleted. Deactivate it instead to hide it from new bookings while keeping historical records intact.',
      deleteTitle: 'Delete {{name}}?',
      deleteBody: 'This permanently removes the channel. It can only be deleted if no booking currently references it.',
    },
    property: {
      noneSelected: 'No property is selected. Choose a property first.',
      loading: 'Loading property profile...',
      error: 'Something went wrong loading the property profile. Please try again.',
      title: 'Property profile',
      subtitle: 'Account and contact details for {{name}}.',
      updatedToast: 'Property profile updated.',
      saveError: 'Could not save the property profile.',
      logo: 'Logo',
      logoAlt: '{{name}} logo',
      logoPreviewError: 'The logo could not be previewed.',
      logoLoading: 'Loading logo preview...',
      logoNone: 'No logo has been uploaded yet.',
      replaceLogo: 'Replace logo',
      uploadLogo: 'Upload logo',
      removeLogo: 'Remove logo',
      logoUploadedToast: 'Logo uploaded.',
      logoUploadError: 'Could not upload the logo.',
      logoRemovedToast: 'Logo removed.',
      logoRemoveError: 'Could not remove the logo.',
      propertyName: 'Property name',
      contactPerson: 'Contact person',
      contactPersonTitle: 'Contact person title',
      contactNumber: 'Contact number',
      contactEmail: 'Contact email',
      contactEmailHelper: 'Shown on receipts as the property contact.',
      currency: 'Currency',
      currencyHelper: 'Used for every amount on this property.',
      receiptLanguage: 'Receipt language',
      receiptLanguageHelper: 'Language receipts are generated in.',
      reviewLink: 'Review link',
      reviewLinkHelper: 'Shown to guests after they submit a review.',
      earliestCheckIn: 'Earliest check-in',
      latestCheckIn: 'Latest check-in',
      earliestCheckout: 'Earliest checkout',
      latestCheckout: 'Latest checkout',
      codePrefix: 'Property code prefix',
      checkInWindow: 'Check-in window',
      checkoutWindow: 'Checkout window',
      windowRange: '{{start}} to {{end}}',
    },
    members: {
      title: 'Members & invitations',
      subtitle: 'Staff with access to this property, and pending invitations.',
      refresh: 'Refresh members',
      invite: 'Invite member',
      loading: 'Loading members...',
      error: 'Something went wrong loading members. Please try again.',
      empty: 'No members yet.',
      membersCaption: 'Members',
      roleForAria: 'Role for {{name}}',
      disableAria: 'Disable {{name}}',
      disable: 'Disable',
      enable: 'Reactivate',
      loadingInvitations: 'Loading pending invitations...',
      errorInvitations: 'Something went wrong loading pending invitations. Please try again.',
      invitationsCaption: 'Pending invitations',
      resendAria: 'Resend invitation to {{email}}',
      revokeAria: 'Revoke invitation for {{email}}',
      resend: 'Resend invitation',
      revoke: 'Revoke',
      inviteTitle: 'Invite member',
      sending: 'Sending...',
      sendInvitation: 'Send invitation',
      memberFallback: 'Member',
      roleChangedToast: '{{name}} is now {{role}}.',
      roleChangeError: "Could not change this member's role.",
      invitationSentToast: 'Invitation sent to {{email}}.',
      inviteError: 'Could not send this invitation.',
      disabledToast: '{{name}} disabled.',
      disableError: 'Could not disable this member.',
      enabledToast: '{{name}} reactivated.',
      enableError: 'Could not reactivate this member.',
      revokedToast: 'Invitation for {{email}} revoked.',
      revokeError: 'Could not revoke this invitation.',
      resentToast: 'Invitation re-sent to {{email}}.',
      resendError: 'Could not resend this invitation.',
      pendingInvitation: 'pending invitation',
      whatsappHelper: 'Defaults to the mobile number when left blank.',
    },
  },
  profile: {
    title: 'My profile',
    subtitle: 'Manage your account details and password.',
    accountDetails: 'Account details',
    replacePicture: 'Replace picture',
    uploadPicture: 'Upload picture',
    takePhoto: 'Take photo',
    remove: 'Remove',
    noRole: 'No role assigned yet',
    platformAdmin: 'Platform administrator',
    displayLanguage: 'Display language',
    emailReadonly: 'Email cannot be changed here.',
    roleHelperNamed: 'Your role at {{name}}, set by an administrator.',
    roleHelper: 'Your role is assigned by an administrator.',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    passwordHelper: 'At least 8 characters, with upper and lower case letters and a number.',
    updatePassword: 'Update password',
    updatingPassword: 'Updating...',
    deactivate: 'Deactivate account',
    deactivateDesc: 'Deactivating signs you out everywhere and blocks sign-in until an administrator reactivates your account.',
    deactivateCta: 'Deactivate my account',
    deactivateTitle: 'Deactivate your account?',
    deactivateBody: "You'll be signed out immediately and won't be able to sign back in until an administrator reactivates your account. Enter your password to confirm.",
    deactivating: 'Deactivating...',
    deactivateConfirm: 'Deactivate account',
    cameraTitle: 'Take profile photo',
    profileUpdatedToast: 'Your profile has been updated.',
    profileUpdateError: 'We could not update your profile. Please try again.',
    photoUpdatedToast: 'Your profile picture has been updated.',
    photoUpdateError: 'We could not update your profile picture. Please try again.',
    photoRemovedToast: 'Your profile picture has been removed.',
    photoRemoveError: 'We could not remove your profile picture. Please try again.',
    deactivatedToast: 'Your account has been deactivated.',
    deactivateError: 'We could not deactivate your account. Please try again.',
    passwordUpdatedToast: 'Your password has been updated.',
    passwordUpdateError: 'We could not update your password. Please try again.',
  },
  notFound: {
    title: 'Page not found',
    message: "The page you're looking for doesn't exist or may have moved.",
    backHome: 'Back to home',
    goDashboard: 'Go to dashboard',
  },
  routeError: {
    title: 'Something went wrong',
    message: 'An unexpected error occurred. Please try again, or head back home.',
  },
  comingSoon: {
    eyebrow: 'COMING SOON',
    title: 'Something new is on the way',
    subtitle: "We're putting the finishing touches on the next part of Nang Book. Leave your email and we'll let you know the moment it's ready.",
    onList: "You're on the list",
    onListDetail: "We'll email <0>{{email}}</0> as soon as we launch.",
    emailAddress: 'Email address',
    submitting: 'Signing you up...',
    notifyMe: 'Notify me at launch',
    backHome: 'Back to home',
    enterEmail: 'Enter an email address so we can reach you.',
    invalidEmail: 'Enter a valid email address.',
    subscribedToast: "You're on the list — we'll email you at launch.",
    genericError: 'Something went wrong. Please try again.',
  },
  legal: {
    lastUpdated: 'Last updated: {{date}}',
    privacy: {
      eyebrow: 'Privacy Policy',
      title: 'Privacy Policy',
      intro: 'This Privacy Policy explains how Nang Book handles information when property teams use the hospitality management platform.',
      s1Title: 'Information We Process',
      s1Body: 'Nang Book may process account details, property information, staff contact details, guest and booking records, payment status, support messages, and technical usage information needed to operate the platform.',
      s2Title: 'How Information Is Used',
      s2Body: 'We use information to provide hospitality management workflows, secure user access, maintain account records, support property teams, improve reliability, and meet legal or operational obligations.',
      s3Title: 'Sharing And Service Providers',
      s3Body: 'Information may be shared with service providers that help deliver hosting, email, analytics, security, support, and payment-related operations. We do not sell customer or guest records.',
      s4Title: 'Security And Retention',
      s4Body: 'We use administrative and technical safeguards designed to protect data. Records are retained only as long as needed for platform operation, compliance, dispute resolution, backups, and legitimate business needs.',
      s5Title: 'Your Choices',
      s5Body: 'Property administrators can manage staff access and request updates or deletion of eligible account information. Some records may need to be retained where required for security, audit, or legal reasons.',
    },
    terms: {
      eyebrow: 'Terms and Condition',
      title: 'Terms and Condition',
      intro: 'These Terms and Condition describe the baseline rules for using Nang Book and managing access to the platform.',
      s1Title: 'Use Of The Platform',
      s1Body: 'Nang Book provides cloud-based hospitality management tools for hotels, guest houses, lodges, and short-stay properties. Users must use the platform only for lawful business operations.',
      s2Title: 'Accounts And Access',
      s2Body: 'Customers are responsible for keeping account credentials secure, assigning appropriate staff access, removing inactive users, and ensuring that information entered into the platform is accurate.',
      s3Title: 'Customer And Guest Data',
      s3Body: 'Property teams remain responsible for the records they create, upload, or manage in Nang Book, including notices, permissions, and legal bases required for guest, booking, and payment information.',
      s4Title: 'Acceptable Use',
      s4Body: 'Users must not misuse the platform, attempt unauthorized access, interfere with service operation, upload malicious content, or use Nang Book to violate laws, rights, or contractual duties.',
      s5Title: 'Service Changes',
      s5Body: 'Nang Book may update features, workflows, security controls, and documentation as the product evolves. Material changes will be communicated through reasonable product or account channels.',
    },
  },
  onboarding: {
    verifyTitle: 'Verify your email',
    verifySubtitle: 'Check your inbox for a verification link before continuing.',
    verifyAlert: 'We sent a verification link to {{email}}. Open it to continue.',
    resendVerification: 'Resend verification email',
    welcome: 'Welcome, {{name}}',
    welcomeSubtitle: 'Create a property to get started, or wait for a staff invitation to join one.',
    createProperty: 'Create a property',
    invitedAlert: 'Been invited to join an existing property instead? Open the invitation link from your email — it will ask you to set a password and then sign in here.',
    acceptedRefresh: "I've just accepted an invitation — refresh",
    createTitle: 'Create your property',
    createSubtitle: "You'll be its first System Administrator, {{name}}.",
    creating: 'Creating...',
    createSubmit: 'Create property',
    createdToast: 'Property created.',
    createError: 'Could not create the property.',
  },
  verifyReceipt: {
    title: 'Verify receipt',
    subtitle: 'Confirm a receipt is genuine — enter its full number or just the last six characters.',
    inputLabel: 'Receipt number or last 6 characters',
    verifying: 'Verifying...',
    verify: 'Verify',
    checking: 'Checking the receipt...',
    notVerified: 'This receipt could not be verified.',
    authentic: 'Authentic receipt',
    receiptNumber: 'Receipt number',
    amountExpected: 'Amount expected',
    amountPaid: 'Amount paid',
    amountRemaining: 'Amount remaining',
    booking: 'Booking',
    issued: 'Issued',
    issuedBy: 'Issued by',
    property: 'Property',
  },
  bookingLink: {
    loading: 'Loading your booking...',
    invalidLink: 'This link is invalid, expired, or has been revoked. Please contact the property directly if you need help with your booking.',
    error: 'Something went wrong loading this link. Please try again.',
    bookingTitle: 'Booking {{code}}',
    shareFeedback: 'Share your feedback',
    thankYou: 'Thank you — your review has been submitted.',
    rating: 'Rating',
    submitting: 'Submitting...',
    submitReview: 'Submit review',
    reviewError: 'Could not submit your review. Please try again.',
  },
  invitation: {
    loading: 'Loading your invitation...',
    invalidInvite: 'This invitation is invalid, expired, or has already been accepted. Ask whoever invited you to send a new one.',
    error: 'Something went wrong loading this invitation. Please try again.',
    joinTitle: 'Join {{name}}',
    joinFallback: 'the property',
    joinSubtitle: 'Confirm your details and set a password to accept this invitation.',
    acceptedNeedsLogin: 'Invitation accepted, but automatic sign-in did not complete. Log in to continue.',
    goToLogin: 'Go to login',
    accepting: 'Accepting...',
    acceptInvitation: 'Accept invitation',
    acceptedWelcome: 'Invitation accepted. Welcome to {{name}}.',
    acceptedLogin: 'Invitation accepted. Please log in to continue.',
    yourProperty: 'your property',
    acceptError: 'Could not accept this invitation. Please try again.',
  },
  platform: {
    title: 'Platform properties',
    subtitle: 'Cross-tenant account oversight, restricted to platform administrators.',
    refresh: 'Refresh properties',
    loading: 'Loading properties...',
    error: 'Something went wrong loading properties. Please try again.',
    empty: 'No properties found.',
    caption: 'Properties',
    active: 'Active',
    suspended: 'Suspended',
    pageOf: 'Page {{current}} of {{total}}',
    detail: {
      loading: 'Loading property...',
      error: 'Something went wrong loading this property. Please try again.',
      updating: 'Updating...',
      suspendProperty: 'Suspend property',
      reactivateProperty: 'Reactivate property',
      reactivatedToast: 'Property reactivated.',
      suspendedToast: 'Property suspended.',
      statusError: "Could not update this property's status.",
    },
  },
  audit: {
    title: 'Audit log',
    subtitle: 'Every write action taken in this workspace, restricted to System Administrators.',
    refresh: 'Refresh audit log',
    system: 'System',
    actionContains: 'Action contains',
    resourceTypeContains: 'Resource type contains',
    loading: 'Loading audit log...',
    error: 'Something went wrong loading the audit log. Please try again.',
    empty: 'No audit entries match these filters.',
    when: 'When',
    user: 'User',
    action: 'Action',
    resource: 'Resource',
    ipAddress: 'IP address',
  },
  cleanup: {
    title: 'Data cleanup',
    subtitle: 'Guest and booking records flagged as missing required information, correctable in bulk.',
    guestsSection: 'Guests needing cleanup',
    bookingsSection: 'Bookings needing cleanup',
    loadingGuests: 'Loading guests...',
    guestsError: 'Something went wrong loading flagged guests.',
    guestsEmpty: 'No guests currently need cleanup.',
    loadingBookings: 'Loading bookings...',
    bookingsError: 'Something went wrong loading bookings.',
    bookingsEmpty: 'No bookings currently need cleanup.',
    saveGuests: 'Save all guest changes',
    saveBookings: 'Save all booking changes',
    guestsSaved: '{{updated}} guest(s) saved.',
    guestsSavedSome: '{{updated}} guest(s) saved, {{failed}} failed.',
    guestsSaveError: 'Could not save guest updates.',
    bookingsSaved: '{{updated}} booking(s) saved.',
    bookingsSavedSome: '{{updated}} booking(s) saved, {{failed}} failed.',
    bookingsSaveError: 'Could not save booking updates.',
    sectionGuest: 'Guest',
    sectionBooking: 'Booking',
    section: 'Section',
    code: 'Code',
    field1: 'Field 1',
    field2: 'Field 2',
    field3: 'Field 3',
    field4: 'Field 4',
    field5: 'Field 5',
  },
  reports: {
    title: 'Reports',
    subtitle: 'Operational reports drawn from booking, financial, and review records.',
    reportLabel: 'Report',
    showUnderlying: 'Show underlying bookings',
    loading: 'Loading report...',
    forbidden: "You don't have permission to view this report.",
    error: 'Something went wrong loading this report. Please try again.',
    noMatchingBookings: 'No matching bookings.',
    noData: 'No data for this range.',
    total: 'Total',
    bookingDetail: '{{checkIn}} to {{checkOut}} · {{status}}',
    paymentsCount: '{{count}} payment(s)',
    colSection: 'Section',
    colLabel: 'Label',
    colValue: 'Value',
    colDetail: 'Detail',
    names: {
      bookingSummary: 'Booking summary (by status)',
      occupancy: 'Occupancy',
      revenue: 'Revenue',
      channel: 'Bookings by channel',
      cancelled: 'Cancelled bookings',
      reviewedVsNot: 'Reviewed vs. not reviewed',
      rating: 'Rating distribution',
      purpose: 'Bookings by stay purpose',
      nationality: 'Guests by nationality',
      cleanup: 'Needs-cleanup summary',
      overduePayments: 'Overdue payments',
      propertyFinancialBreakdown: 'Property financial breakdown',
    },
    sections: {
      status: 'Status',
      occupancy: 'Occupancy',
      revenue: 'Revenue',
      channel: 'Channel',
      cancelled: 'Cancelled',
      reviews: 'Reviews',
      ratings: 'Ratings',
      stayPurpose: 'Stay purpose',
      nationality: 'Nationality',
      cleanup: 'Cleanup',
      overduePayments: 'Overdue payments',
      financialBreakdown: 'Financial breakdown',
      paymentMethod: 'Payment method',
      method: 'Method',
    },
    stats: {
      totalRoomNights: 'Total room-nights',
      totalOccupants: 'Total occupants',
      avgOccupants: 'Avg. occupants / booking',
      bookingsCounted: 'Bookings counted',
      expectedTotal: 'Expected total',
      actualTotal: 'Actual total',
      actualTotalAgreed: 'Actual total (agreed)',
      collectedTotal: 'Collected total',
      recordsCounted: 'Records counted',
      cancelledBookings: 'Cancelled bookings',
      reviewed: 'Reviewed',
      notReviewed: 'Not reviewed',
      averageRating: 'Average rating',
      noRatings: 'No ratings yet',
      ratingsCounted: 'Ratings counted',
      guestsNeedingCleanup: 'Guests needing cleanup',
      bookingsNeedingCleanup: 'Bookings needing cleanup',
      overdueBookings: 'Overdue bookings',
      receivedTotal: 'Received total',
      outstanding: 'Outstanding',
      overpaid: 'Overpaid',
      paymentsCounted: 'Payments counted',
    },
    captions: {
      cancelledBookings: 'Cancelled bookings',
      reviewedBookings: 'Reviewed bookings',
      notReviewedBookings: 'Not reviewed bookings',
      notYetReviewedBookings: 'Not-yet-reviewed bookings',
      overdueBookings: 'Overdue bookings',
    },
    automation: {
      title: 'Payment automation',
      dryRun: 'Dry run',
      sendDigest: 'Send digest',
      autoCancelDays: 'Auto-cancel after days',
      running: 'Running...',
      run: 'Run automation',
      summaryDry: '{{overdue}} overdue booking(s) detected. {{cancelled}} booking(s) would be auto-cancelled.',
      summaryReal: '{{overdue}} overdue booking(s) detected. {{cancelled}} booking(s) auto-cancelled.',
      invalidDays: 'Auto-cancel days must be a positive whole number.',
      dryComplete: 'Payment automation dry run complete.',
      complete: 'Payment automation complete.',
      error: 'Could not run payment automation.',
    },
  },
};
